import React from 'react';
import { Dropdown, Form } from '@edgebox/react-components';
import { useLocation, useNavigate } from 'react-router';
import { InternalId, IPagedListResponse, StaticReference, Uuid } from '@edgebox/data-definition-kit';
import {
  SyncCoreDataDefinitionsEnumTranslator,
  MonitoringPromptCountry,
  MonitoringPromptStatus,
  MonitoringPromptType,
  MonitoringPromptRunStatus,
  TaskGroupType,
  TaskStatus,
  TaxonomyFacet,
  TaxonomyTermTargetType,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientMonitoringPromptDraft,
  ClientMonitoringPromptEntity,
  ClientMonitoringPromptRunEntity,
  ClientTaskGroupDraft,
} from '@edgebox/sync-core-rest-client';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { AppParamsContext, IAppParams } from '../../AppParams';
import {
  ensureMonitoringPromptDraftDefaults,
  ensureMonitoringPromptEntityDefaults,
  IMonitoringTermOption,
  ITaxonomyDropdownOption,
  MonitoringPromptDraftEditor,
  MonitoringPromptEntityEditor,
} from './Components/MonitoringPromptForm';
import { MonitoringPromptInstanceName } from './Components/MonitoringPromptInstanceName';
import { MonitoringPromptRunDetail, MonitoringPromptRunStatusBadge } from './Components/MonitoringPromptRunDetail';
import { NO_PURPOSE_KPI_MASK } from './Components/purposeKpiMask.utils';
import { TaxonomyTermNameBadge } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { resolveProjectSite } from './Components/projectSite.utils';
import { useGovernanceRouteSync } from './useGovernanceRouteSync';
import { faPlus, faSatelliteDish } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { BrandPresenceEmptyState } from './Components/BrandPresenceEmptyState';
import { SkeletonBlock, SkeletonRegion } from './Components/BrandPresenceSkeleton';
import {
  BP_CARD_CLASSES,
  BP_PRIMARY_BUTTON_CLASSES,
  BP_PRIMARY_BUTTON_SMALL_CLASSES,
  BP_TOOLBAR_BUTTON_CLASSES,
  BP_TOOLBAR_BUTTON_SMALL_CLASSES,
} from './Components/brandPresenceClasses';

type StatusFilter = 'all' | MonitoringPromptStatus;
type TypeFilter = 'all' | MonitoringPromptType;
type MonitoringPromptRouteMode = 'list' | 'runs' | 'run';

interface IMonitoringPromptRouteState {
  mode: MonitoringPromptRouteMode;
  promptId?: string;
  runId?: string;
}

interface IMonitoringPromptStatusAction {
  label: string;
  nextStatus: MonitoringPromptStatus;
}

const NON_PREVIOUS_STATUSES = [
  MonitoringPromptStatus.Draft,
  MonitoringPromptStatus.Active,
  MonitoringPromptStatus.Paused,
  MonitoringPromptStatus.Archived,
];

type TMonitoringPromptEnumName =
  | 'MonitoringPromptStatus'
  | 'MonitoringPromptType'
  | 'MonitoringPromptCountry'
  | 'TaxonomyFacet'
  | 'MonitoringPromptRunStatus'
  | 'TaskStatus';

function translateEnumValue(enumName: TMonitoringPromptEnumName, value: string): string {
  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(enumName, value as any);
  } catch {
    return value;
  }
}

function getStableKey(reference: unknown): string {
  if (!reference || typeof reference !== 'object') {
    return '';
  }

  const referenceRecord = reference as {
    getStableKey?: () => string;
    key?: unknown;
  };

  return String(referenceRecord.getStableKey?.() || referenceRecord.key || '').trim();
}

function formatDate(value?: number | { format: (template: string) => string } | null) {
  if (!value) {
    return '-';
  }

  if (typeof value === 'number') {
    return new Date(value).toLocaleString();
  }

  return value.format('MMM DD, HH:mm');
}

/** Prompt cards drawn while the first page loads: name and status, the prompt text, tags, the run line. */
const PROMPT_SKELETON_CARDS = [0, 1, 2].map((card) => (
  <div key={card} aria-hidden="true" className={`${BP_CARD_CLASSES} space-y-3 p-5`}>
    <div className="flex items-center gap-2">
      <SkeletonBlock className={`h-5 ${card % 2 ? 'w-52' : 'w-72'}`} />
      <SkeletonBlock className="h-5 w-16 rounded-full" />
      <SkeletonBlock className="h-5 w-20 rounded-full" />
    </div>
    <SkeletonBlock className="h-4 w-full max-w-2xl" />
    <div className="flex gap-2">
      <SkeletonBlock className="h-6 w-20 rounded-full" />
      <SkeletonBlock className="h-6 w-24 rounded-full" />
    </div>
    <SkeletonBlock className="h-3 w-80 max-w-full" />
  </div>
));

/** Run rows drawn while the runs of a prompt load, in the columns of the run table. */
const RUN_SKELETON_ROWS = [0, 1, 2, 3].map((row) => (
  <div key={row} aria-hidden="true" className="grid grid-cols-12 items-center gap-3 px-4 py-3">
    <SkeletonBlock className="col-span-2 h-5 w-20 rounded-full" />
    <SkeletonBlock className="col-span-3 h-5 w-24 rounded-full" />
    <SkeletonBlock className="col-span-3 h-3.5 w-24" />
    <SkeletonBlock className="col-span-3 h-3.5 w-24" />
    <SkeletonBlock className="col-span-1 ml-auto h-3.5 w-6" />
  </div>
));

/** Instance rows drawn while the instances of a template load. */
const INSTANCE_SKELETON_ROWS = [0, 1].map((row) => (
  <div key={row} aria-hidden="true" className="space-y-2 px-5 py-3">
    <SkeletonBlock className="h-4 w-56" />
    <SkeletonBlock className="h-3 w-96 max-w-full" />
  </div>
));

function statusClass(status?: MonitoringPromptStatus) {
  if (status === MonitoringPromptStatus.Active) {
    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }

  if (status === MonitoringPromptStatus.Paused) {
    return 'bg-amber-50 border-amber-200 text-amber-700';
  }

  if (status === MonitoringPromptStatus.Archived) {
    return 'bg-slate-100 border-slate-200 text-slate-600';
  }

  return 'bg-sky-50 border-sky-200 text-sky-700';
}

function isTaskStatusRunning(status?: string) {
  return status === TaskStatus.Initializing || status === TaskStatus.Running || status === TaskStatus.Retrying;
}

function getPromptStatusActions(status?: MonitoringPromptStatus): IMonitoringPromptStatusAction[] {
  if (status === MonitoringPromptStatus.Draft) {
    return [
      { label: 'Activate', nextStatus: MonitoringPromptStatus.Active },
      { label: 'Archive', nextStatus: MonitoringPromptStatus.Archived },
    ];
  }

  if (status === MonitoringPromptStatus.Active) {
    return [
      { label: 'Pause', nextStatus: MonitoringPromptStatus.Paused },
      { label: 'Archive', nextStatus: MonitoringPromptStatus.Archived },
    ];
  }

  if (status === MonitoringPromptStatus.Paused) {
    return [
      { label: 'Unpause', nextStatus: MonitoringPromptStatus.Active },
      { label: 'Archive', nextStatus: MonitoringPromptStatus.Archived },
    ];
  }

  if (status === MonitoringPromptStatus.Archived) {
    return [{ label: 'Reactivate', nextStatus: MonitoringPromptStatus.Active }];
  }

  return [];
}

/**
 * Folds a freshly loaded page into the rows already on screen. A row the new page repeats — the
 * backend's paging can shift a row across a page boundary while the reader is working — replaces
 * the copy already held rather than appearing twice; everything else keeps the order it arrived in.
 */
function mergeLoadedPrompts(
  current: ClientMonitoringPromptEntity[],
  incoming: ClientMonitoringPromptEntity[]
): ClientMonitoringPromptEntity[] {
  if (!current.length) {
    return incoming;
  }

  const indexById = new Map(current.map((prompt, index) => [String(prompt.id), index]));
  const merged = [...current];
  const appended: ClientMonitoringPromptEntity[] = [];

  for (const prompt of incoming) {
    const id = String(prompt.id);
    const existingIndex = indexById.get(id);

    if (existingIndex === undefined) {
      indexById.set(id, merged.length + appended.length);
      appended.push(prompt);
      continue;
    }

    if (existingIndex < merged.length) {
      merged[existingIndex] = prompt;
    } else {
      appended[existingIndex - merged.length] = prompt;
    }
  }

  return [...merged, ...appended];
}

function canUpdatePromptStatus(prompt?: ClientMonitoringPromptEntity): boolean {
  if (!prompt) {
    return false;
  }

  if (prompt.type === MonitoringPromptType.Template) {
    return true;
  }

  if (prompt.type !== MonitoringPromptType.Instance) {
    return false;
  }

  return !prompt.templatePrompt?.getStableKey();
}

function defaultPromptDraft(projectId?: InternalId) {
  const draft = new ClientMonitoringPromptDraft();
  draft.name = '';
  draft.promptText = '';
  draft.status = MonitoringPromptStatus.Active;
  draft.type = MonitoringPromptType.Instance;
  draft.frequencyHours = 24;
  draft.engines = [];
  draft.locales = [];
  draft.countries = [];
  draft.templateTerms = [];
  draft.staticTerms = [];
  draft.terms = [];
  draft.targetTerms = [];
  if (projectId) {
    Object.assign(draft, {
      project: new StaticReference({ id: projectId }),
    });
  }
  ensureMonitoringPromptDraftDefaults(draft);
  return draft;
}

function extractCountryFromLocaleKey(localeKey: string, availableCountryCodes: Set<string>): MonitoringPromptCountry | undefined {
  if (!localeKey) {
    return undefined;
  }

  const normalized = String(localeKey).trim();
  if (!normalized) {
    return undefined;
  }

  const directMatch = normalized.toUpperCase();
  if (availableCountryCodes.has(directMatch)) {
    return directMatch as MonitoringPromptCountry;
  }

  const pieces = normalized.split(/[-_:.]/).filter((piece) => !!piece);
  for (let index = pieces.length - 1; index >= 0; index -= 1) {
    const candidate = pieces[index].toUpperCase();
    if (candidate.length === 2 && availableCountryCodes.has(candidate)) {
      return candidate as MonitoringPromptCountry;
    }
  }

  return undefined;
}

function parseMonitoringPromptRoute(pathname: string): IMonitoringPromptRouteState {
  const segments = pathname.split('/').filter((segment) => !!segment);

  let promptId: string | undefined;
  let action: string | undefined;

  if (segments[0] === 'brand-presence' && segments[1] === 'settings' && segments[2] === 'monitoring') {
    promptId = segments[3];
    action = segments[4];
  } else {
    return { mode: 'list' };
  }

  if (!promptId || !action) {
    return { mode: 'list' };
  }

  if (action === 'runs') {
    return {
      mode: 'runs',
      promptId: decodeURIComponent(promptId),
      runId: segments[5] ? decodeURIComponent(segments[5]) : undefined,
    };
  }

  if (action === 'run') {
    return { mode: 'run', promptId: decodeURIComponent(promptId) };
  }

  return { mode: 'list' };
}

export function GovernanceMonitoringPrompts() {
  const apiContext = React.useContext(SyncCoreApiContext);
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const api = apiContext?.api;
  const location = useLocation();
  const navigate = useNavigate();

  const projectUuid = appParams?.pageOptions?.projectUuid as Uuid | undefined;
  const appMonitoringPromptsPrefix = '/brand-presence/settings/monitoring';
  const routeState = React.useMemo(() => parseMonitoringPromptRoute(location.pathname), [location.pathname]);

  const [page, setPage] = React.useState(0);
  const [itemsPerPage] = React.useState(20);
  // Every page the reader has asked for, in the order it arrived. The list renders from this rather
  // than from the newest response, so asking for more fetches only the next page and leaves the rows
  // already on screen untouched.
  const [loadedPrompts, setLoadedPrompts] = React.useState<ClientMonitoringPromptEntity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [response, setResponse] = React.useState<IPagedListResponse<ClientMonitoringPromptEntity> | undefined>(undefined);

  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');
  const [countryFilter, setCountryFilter] = React.useState<MonitoringPromptCountry | 'all'>('all');
  const [engineFilterKey, setEngineFilterKey] = React.useState<string>('');
  const [localeFilterKey, setLocaleFilterKey] = React.useState<string>('');

  const [syncCoreProjectId, setSyncCoreProjectId] = React.useState<InternalId | undefined>(undefined);

  const [localeOptions, setLocaleOptions] = React.useState<IMonitoringTermOption[]>([]);
  const [engineOptions, setEngineOptions] = React.useState<IMonitoringTermOption[]>([]);
  const [templateTermOptions, setTemplateTermOptions] = React.useState<IMonitoringTermOption[]>([]);
  const [taxonomyOptions, setTaxonomyOptions] = React.useState<ITaxonomyDropdownOption[]>([]);

  const enforceLocaleCountryFilter = false;

  const localeDerivedCountries = React.useMemo(() => {
    const countryCodes = new Set(Object.values(MonitoringPromptCountry));
    const countriesByCode = new Set<MonitoringPromptCountry>();

    for (const localeOption of localeOptions) {
      const country = extractCountryFromLocaleKey(localeOption.key, countryCodes);
      if (country) {
        countriesByCode.add(country);
      }
    }

    return Object.values(MonitoringPromptCountry).filter((country) => countriesByCode.has(country));
  }, [localeOptions]);

  const availableCountries = React.useMemo(
    () => (enforceLocaleCountryFilter ? localeDerivedCountries : Object.values(MonitoringPromptCountry)),
    [enforceLocaleCountryFilter, localeDerivedCountries]
  );

  const [createDraft, setCreateDraft] = React.useState<ClientMonitoringPromptDraft | undefined>(undefined);
  const [editPrompt, setEditPrompt] = React.useState<ClientMonitoringPromptEntity | undefined>(undefined);
  const [editPromptId, setEditPromptId] = React.useState<InternalId | undefined>(undefined);
  const [routePrompt, setRoutePrompt] = React.useState<ClientMonitoringPromptEntity | undefined>(undefined);
  const [routePromptLoading, setRoutePromptLoading] = React.useState(false);
  const [routePromptError, setRoutePromptError] = React.useState<string | undefined>(undefined);

  const [runsPage, setRunsPage] = React.useState(0);
  const [runsLoading, setRunsLoading] = React.useState(false);
  const [runsError, setRunsError] = React.useState<string | undefined>(undefined);
  const [runs, setRuns] = React.useState<ClientMonitoringPromptRunEntity[]>([]);
  const [runsNumberOfPages, setRunsNumberOfPages] = React.useState(1);
  const [citationCountByRunId, setCitationCountByRunId] = React.useState<Record<string, number>>({});

  const [expandedTemplateKeys, setExpandedTemplateKeys] = React.useState<Record<string, boolean>>({});
  const [templateInstances, setTemplateInstances] = React.useState<Record<string, ClientMonitoringPromptEntity[]>>({});
  const [templateInstanceLoading, setTemplateInstanceLoading] = React.useState<Record<string, boolean>>({});
  const [templateInstanceCounts, setTemplateInstanceCounts] = React.useState<Record<string, number>>({});

  const [executeLoading, setExecuteLoading] = React.useState(false);
  const [executeError, setExecuteError] = React.useState<string | undefined>(undefined);
  const [executeTaskGroupId, setExecuteTaskGroupId] = React.useState<InternalId | undefined>(undefined);
  const [executeProgress, setExecuteProgress] = React.useState<string | undefined>(undefined);
  const [executeLatestRun, setExecuteLatestRun] = React.useState<ClientMonitoringPromptRunEntity | undefined>(undefined);
  const [statusUpdateByPromptId, setStatusUpdateByPromptId] = React.useState<Record<string, boolean>>({});

  const executePollingRef = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const listRequestIdRef = React.useRef(0);
  const attemptedProjectUuidRef = React.useRef<string | undefined>(undefined);

  useGovernanceRouteSync({
    embedPrefix: '/brand-presence/settings/monitoring',
    appPrefix: appMonitoringPromptsPrefix,
    defaultEmbedPath: '/brand-presence/settings/monitoring',
  });

  const loadMonitoringPromptPage = React.useCallback(async () => {
    if (!api) {
      setLoading(false);
      return;
    }

    const requestId = ++listRequestIdRef.current;
    setLoading(true);
    setError(undefined);

    try {
      const filters = {
        status: statusFilter === 'all' ? NON_PREVIOUS_STATUSES : [statusFilter],
        type: typeFilter === 'all' ? undefined : [typeFilter],
        countries: countryFilter === 'all' ? undefined : [countryFilter],
        enginesKey: engineFilterKey || undefined,
        // Filters on the target country/language pairs the prompt monitors in (`localesKey`),
        // not on the prompt's authored locale.
        localesKey: localeFilterKey || undefined,
      };

      const result = await api.governance.monitoringPrompts.list({ page, itemsPerPage }, filters);
      if (requestId !== listRequestIdRef.current) {
        return;
      }
      setResponse(result);
      // The first page is the whole list starting over; any later page extends what is on screen.
      setLoadedPrompts((current) => (result.page === 0 ? result.items || [] : mergeLoadedPrompts(current, result.items || [])));
    } catch (requestError) {
      if (requestId !== listRequestIdRef.current) {
        return;
      }
      setError((requestError as Error)?.message || 'Failed to load monitoring prompts.');
    } finally {
      if (requestId === listRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [api, countryFilter, engineFilterKey, itemsPerPage, localeFilterKey, page, statusFilter, typeFilter]);

  React.useEffect(() => {
    void loadMonitoringPromptPage();
  }, [loadMonitoringPromptPage]);

  React.useEffect(() => {
    setPage(0);
    setLoadedPrompts([]);
  }, [statusFilter, typeFilter, countryFilter, engineFilterKey, localeFilterKey]);

  React.useEffect(() => {
    if (!enforceLocaleCountryFilter) {
      return;
    }

    if (countryFilter !== 'all' && !availableCountries.includes(countryFilter)) {
      setCountryFilter('all');
    }
  }, [availableCountries, countryFilter, enforceLocaleCountryFilter]);

  const projectUuidKey = React.useMemo(() => String(projectUuid || ''), [projectUuid]);

  React.useEffect(() => {
    if (!api || !projectUuidKey) {
      attemptedProjectUuidRef.current = undefined;
      setSyncCoreProjectId(undefined);
      return;
    }

    if (attemptedProjectUuidRef.current === projectUuidKey) {
      return;
    }

    attemptedProjectUuidRef.current = projectUuidKey;

    let active = true;

    void (async () => {
      try {
        const project = await api.service.projects.itemByUuid(projectUuid as Uuid);
        if (!active) {
          return;
        }

        setSyncCoreProjectId(project.id as InternalId);
      } catch {
        if (!active) {
          return;
        }

        setSyncCoreProjectId(undefined);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, projectUuid, projectUuidKey]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;

    void (async () => {
      try {
        const [taxonomies, localeTerms, engineTerms] = await Promise.all([
          api.service.taxonomies.list({ page: 0, itemsPerPage: 500 }),
          api.service.taxonomyTerms.list({ page: 0, itemsPerPage: 500 }, { taxonomyFacet: TaxonomyFacet.Locale }),
          api.service.taxonomyTerms.list({ page: 0, itemsPerPage: 500 }, { taxonomyFacet: TaxonomyFacet.Engine }),
        ]);

        if (!active) {
          return;
        }

        const monitoringFacets = new Set(
          (taxonomies.items || [])
            .filter((taxonomy) => (taxonomy.allowedTargets || []).includes(TaxonomyTermTargetType.Governance_MonitoringPrompt))
            .map((taxonomy) => taxonomy.facet)
            .filter((facet): facet is TaxonomyFacet => !!facet)
        );

        setTaxonomyOptions(
          (taxonomies.items || [])
            .filter((taxonomy) => (taxonomy.allowedTargets || []).includes(TaxonomyTermTargetType.Governance_MonitoringPrompt))
            .filter((taxonomy) => !!taxonomy.facet)
            .map((taxonomy) => ({
              key: String(taxonomy.key || ''),
              name: taxonomy.name,
              facet: taxonomy.facet as TaxonomyFacet,
            }))
        );

        const templateFacets = Array.from(monitoringFacets).filter(
          (facet) => facet !== TaxonomyFacet.Locale && facet !== TaxonomyFacet.Engine
        );

        const templateTerms = (
          await Promise.all(
            templateFacets.map(async (facet) => {
              const result = await api.service.taxonomyTerms.list({ page: 0, itemsPerPage: 500 }, { taxonomyFacet: facet });
              return result.items || [];
            })
          )
        ).flat();

        if (!active) {
          return;
        }

        setLocaleOptions(
          (localeTerms.items || []).map((term) => ({
            id: term.id as InternalId,
            key: term.key,
            name: term.name,
            facet: term.taxonomy?.facet as TaxonomyFacet | undefined,
            taxonomyId: String(term.taxonomy?.reference?.getId?.() || ''),
            taxonomyKey: String(term.taxonomy?.key || ''),
          }))
        );

        setEngineOptions(
          (engineTerms.items || []).map((term) => ({
            id: term.id as InternalId,
            key: term.key,
            name: term.name,
            facet: term.taxonomy?.facet as TaxonomyFacet | undefined,
            taxonomyId: String(term.taxonomy?.reference?.getId?.() || ''),
            taxonomyKey: String(term.taxonomy?.key || ''),
          }))
        );

        setTemplateTermOptions(
          templateTerms.map((term) => ({
            id: term.id as InternalId,
            key: term.key,
            name: term.name,
            facet: term.taxonomy?.facet as TaxonomyFacet | undefined,
            taxonomyId: String(term.taxonomy?.reference?.getId?.() || ''),
            taxonomyKey: String(term.taxonomy?.key || ''),
          }))
        );
      } catch {
        if (!active) {
          return;
        }

        setLocaleOptions([]);
        setEngineOptions([]);
        setTemplateTermOptions([]);
        setTaxonomyOptions([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [api]);

  React.useEffect(() => {
    if (!api || routeState.mode === 'list' || !routeState.promptId) {
      setRoutePrompt(undefined);
      setRoutePromptError(undefined);
      setRoutePromptLoading(false);
      return;
    }

    let active = true;

    void (async () => {
      try {
        setRoutePromptLoading(true);
        setRoutePromptError(undefined);
        const loaded = await api.governance.monitoringPrompts.item(routeState.promptId as InternalId, true);
        if (!active) {
          return;
        }

        setRoutePrompt(loaded);
      } catch (requestError) {
        if (!active) {
          return;
        }

        setRoutePrompt(undefined);
        setRoutePromptError((requestError as Error)?.message || 'Failed to load monitoring prompt.');
      } finally {
        if (active) {
          setRoutePromptLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, routeState.mode, routeState.promptId]);

  React.useEffect(() => {
    if (routeState.mode !== 'runs') {
      setRuns([]);
      setRunsError(undefined);
      setRunsLoading(false);
      setRunsPage(0);
      setRunsNumberOfPages(1);
      setCitationCountByRunId({});
      return;
    }

    setRunsPage(0);
  }, [routeState.mode, routeState.promptId]);

  React.useEffect(() => {
    if (!api || routeState.mode !== 'runs' || !routePrompt?.key) {
      return;
    }

    let active = true;

    void (async () => {
      try {
        setRunsLoading(true);
        setRunsError(undefined);

        const response = await api.governance.monitoringPromptRuns.list(
          { page: runsPage, itemsPerPage: 10 },
          {
            promptKey: routePrompt.key,
          }
        );

        if (!active) {
          return;
        }

        const items = response.items || [];
        setRuns(items);
        setRunsNumberOfPages(Math.max(1, response.numberOfPages || 1));
      } catch (requestError) {
        if (!active) {
          return;
        }

        setRunsError((requestError as Error)?.message || 'Failed to load runs.');
      } finally {
        if (active) {
          setRunsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, routePrompt?.key, routeState.mode, runsPage]);

  React.useEffect(() => {
    if (!runs.length) {
      setCitationCountByRunId({});
      return;
    }

    let active = true;

    void (async () => {
      const entries = await Promise.all(
        runs.map(async (run) => {
          const runId = String(run.id || '');
          if (!runId || !run.parsedFile?.get) {
            return [runId, 0] as const;
          }

          try {
            const file = await run.parsedFile.get();
            const downloadUrl = file?.downloadUrl;
            if (!downloadUrl) {
              return [runId, 0] as const;
            }

            const response = await fetch(downloadUrl);
            const content = await response.text();
            const parsedUnknown = JSON.parse(content) as { citations?: unknown; output?: { citations?: unknown } };
            const citations = Array.isArray(parsedUnknown?.citations)
              ? parsedUnknown.citations
              : Array.isArray(parsedUnknown?.output?.citations)
                ? parsedUnknown.output.citations
                : [];

            return [runId, citations.length] as const;
          } catch {
            return [runId, 0] as const;
          }
        })
      );

      if (!active) {
        return;
      }

      const next: Record<string, number> = {};
      for (const [runId, count] of entries) {
        if (runId) {
          next[runId] = count;
        }
      }

      setCitationCountByRunId(next);
    })();

    return () => {
      active = false;
    };
  }, [runs]);

  React.useEffect(() => {
    if (routeState.mode !== 'runs' || !routeState.promptId || !runs.length) {
      return;
    }

    const hasSelected = !!routeState.runId && runs.some((run) => String(run.id) === routeState.runId);
    if (hasSelected) {
      return;
    }

    const firstRun = runs[0];
    if (!firstRun?.id) {
      return;
    }

    navigate(
      `/brand-presence/settings/monitoring/${encodeURIComponent(routeState.promptId)}/runs/${encodeURIComponent(String(firstRun.id))}`,
      { replace: true }
    );
  }, [navigate, routeState.mode, routeState.promptId, routeState.runId, runs]);

  const selectedRun = React.useMemo(() => runs.find((run) => String(run.id) === routeState.runId) || runs[0], [runs, routeState.runId]);

  const visibleItems = loadedPrompts;

  const templateItems = React.useMemo(() => visibleItems.filter((item) => item.type === MonitoringPromptType.Template), [visibleItems]);
  const standaloneInstances = React.useMemo(
    () => visibleItems.filter((item) => item.type === MonitoringPromptType.Instance && !item.templatePrompt?.getStableKey?.()),
    [visibleItems]
  );

  const promptItems = React.useMemo(() => [...templateItems, ...standaloneInstances], [standaloneInstances, templateItems]);

  const localeNameByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const option of localeOptions) {
      map.set(option.key, option.name);
    }
    return map;
  }, [localeOptions]);

  const termNameByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const option of templateTermOptions) {
      map.set(option.key, option.name);
    }
    return map;
  }, [templateTermOptions]);

  React.useEffect(() => {
    if (!api || !templateItems.length) {
      return;
    }

    const templateKeysToLoad = templateItems
      .map((item) => item.key)
      .filter((templateKey) => templateKey && templateInstanceCounts[templateKey] === undefined);

    if (!templateKeysToLoad.length) {
      return;
    }

    let active = true;

    void (async () => {
      const countEntries = await Promise.all(
        templateKeysToLoad.map(async (templateKey) => {
          try {
            const result = await api.governance.monitoringPrompts.list(
              { page: 0, itemsPerPage: 0 },
              {
                type: [MonitoringPromptType.Instance],
                status: NON_PREVIOUS_STATUSES,
                templatePromptKey: templateKey,
              }
            );

            return [templateKey, Number(result.totalNumberOfItems || 0)] as const;
          } catch {
            return [templateKey, 0] as const;
          }
        })
      );

      if (!active) {
        return;
      }

      setTemplateInstanceCounts((current) => {
        const next = { ...current };
        for (const [templateKey, count] of countEntries) {
          next[templateKey] = count;
        }
        return next;
      });
    })();

    return () => {
      active = false;
    };
  }, [api, templateInstanceCounts, templateItems]);

  const toggleTemplateExpansion = React.useCallback(
    async (template: ClientMonitoringPromptEntity) => {
      const templateKey = template.key;
      const isExpanded = !!expandedTemplateKeys[templateKey];

      setExpandedTemplateKeys((current) => ({
        ...current,
        [templateKey]: !isExpanded,
      }));

      if (isExpanded || templateInstances[templateKey] || !api) {
        return;
      }

      setTemplateInstanceLoading((current) => ({
        ...current,
        [templateKey]: true,
      }));

      try {
        const instances = await api.governance.monitoringPrompts.list(
          { page: 0, itemsPerPage: 100 },
          {
            type: [MonitoringPromptType.Instance],
            status: NON_PREVIOUS_STATUSES,
            templatePromptKey: templateKey,
          }
        );

        setTemplateInstances((current) => ({
          ...current,
          [templateKey]: instances.items || [],
        }));
        setTemplateInstanceCounts((current) => ({
          ...current,
          [templateKey]: Number(instances.totalNumberOfItems || (instances.items || []).length || 0),
        }));
      } finally {
        setTemplateInstanceLoading((current) => ({
          ...current,
          [templateKey]: false,
        }));
      }
    },
    [api, expandedTemplateKeys, templateInstances]
  );

  const refreshPrompt = React.useCallback(
    async (prompt: ClientMonitoringPromptEntity, options?: { activateInlineEdit?: boolean }) => {
      if (!api) {
        return;
      }

      const reloaded = await api.governance.monitoringPrompts.item(prompt.id as InternalId, true);
      setEditPrompt(reloaded);
      if (options?.activateInlineEdit && reloaded.id) {
        setEditPromptId(reloaded.id as InternalId);
      }
    },
    [api]
  );

  const replacePromptInLocalState = React.useCallback((updated: ClientMonitoringPromptEntity) => {
    const updatedId = String(updated.id || '');
    const updatedKey = String(updated.key || '');

    const isSamePrompt = (candidate?: ClientMonitoringPromptEntity) => {
      if (!candidate) {
        return false;
      }

      const candidateId = String(candidate.id || '');
      if (updatedId && candidateId && candidateId === updatedId) {
        return true;
      }

      const candidateKey = String(candidate.key || '');
      return !!updatedKey && !!candidateKey && candidateKey === updatedKey;
    };

    setResponse((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        items: (current.items || []).map((item) => (isSamePrompt(item) ? updated : item)),
      };
    });

    // The list renders from the accumulated pages, so a row only visibly changes once it is
    // replaced here too.
    setLoadedPrompts((current) => current.map((prompt) => (isSamePrompt(prompt) ? updated : prompt)));

    setTemplateInstances((current) => {
      const next: Record<string, ClientMonitoringPromptEntity[]> = {};
      for (const [templateKey, instances] of Object.entries(current)) {
        next[templateKey] = (instances || []).map((instance) => (isSamePrompt(instance) ? updated : instance));
      }
      return next;
    });

    setRoutePrompt((current) => (isSamePrompt(current) ? updated : current));
    setEditPrompt((current) => (isSamePrompt(current) ? updated : current));
  }, []);

  const updatePromptStatus = React.useCallback(
    async (prompt: ClientMonitoringPromptEntity, nextStatus: MonitoringPromptStatus) => {
      if (!api || !prompt.id) {
        return;
      }

      if (!canUpdatePromptStatus(prompt)) {
        return;
      }

      if (prompt.status === nextStatus) {
        return;
      }

      const promptId = String(prompt.id);

      setStatusUpdateByPromptId((current) => ({
        ...current,
        [promptId]: true,
      }));

      try {
        const payload = new ClientMonitoringPromptEntity({
          ...prompt,
          status: nextStatus,
        });
        ensureMonitoringPromptEntityDefaults(payload);

        const updated = await api.governance.monitoringPrompts.update(payload);
        replacePromptInLocalState(updated);
      } finally {
        setStatusUpdateByPromptId((current) => ({
          ...current,
          [promptId]: false,
        }));
      }
    },
    [api, replacePromptInLocalState]
  );

  const updateMonitoringPrompt = React.useCallback(
    async (entity: ClientMonitoringPromptEntity, options?: { updateTranslations?: boolean }) => {
      if (!api) {
        throw new Error('Missing API context.');
      }

      const updateWithFlags = api.governance.monitoringPrompts.update as (
        prompt: ClientMonitoringPromptEntity,
        flags?: { updateTranslations?: boolean }
      ) => Promise<ClientMonitoringPromptEntity>;

      return updateWithFlags(entity, {
        updateTranslations: !!options?.updateTranslations,
      });
    },
    [api]
  );

  const invalidateTemplateInstanceCache = React.useCallback((templateKey?: string) => {
    if (!templateKey) {
      setExpandedTemplateKeys({});
      setTemplateInstances({});
      setTemplateInstanceLoading({});
      setTemplateInstanceCounts({});
      return;
    }

    setExpandedTemplateKeys((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, templateKey)) {
        return current;
      }

      const next = { ...current };
      delete next[templateKey];
      return next;
    });

    setTemplateInstances((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, templateKey)) {
        return current;
      }

      const next = { ...current };
      delete next[templateKey];
      return next;
    });

    setTemplateInstanceLoading((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, templateKey)) {
        return current;
      }

      const next = { ...current };
      delete next[templateKey];
      return next;
    });

    setTemplateInstanceCounts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, templateKey)) {
        return current;
      }

      const next = { ...current };
      delete next[templateKey];
      return next;
    });
  }, []);

  const renderPromptCard = React.useCallback(
    (prompt: ClientMonitoringPromptEntity, options?: { nested?: boolean; flat?: boolean }) => {
      const nested = !!options?.nested;
      const flat = !!options?.flat;
      const isInlineEditing = !!editPromptId && String(editPromptId) === String(prompt.id) && !!editPrompt;

      if (isInlineEditing && editPrompt) {
        const editContainerClass = flat ? 'p-5' : `${BP_CARD_CLASSES} p-5 ${nested ? 'ml-6' : ''}`;

        return (
          <div key={prompt.id} className={editContainerClass}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="m-0 text-base font-bold text-slate-900">Edit monitoring prompt</h3>
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                onClick={() => {
                  setEditPrompt(undefined);
                  setEditPromptId(undefined);
                }}
              >
                Cancel
              </button>
            </div>

            {api ? (
              <MonitoringPromptEntityEditor
                api={api}
                initialValues={editPrompt}
                submitLabel="Save"
                taxonomyOptions={taxonomyOptions}
                localeOptions={localeOptions}
                availableCountries={availableCountries}
                engineOptions={engineOptions}
                templateTermOptions={templateTermOptions}
                onSubmit={async (entity) => {
                  ensureMonitoringPromptEntityDefaults(entity);
                  const updated = await updateMonitoringPrompt(entity, {
                    updateTranslations: entity.type === MonitoringPromptType.Template,
                  });

                  if (updated.type === MonitoringPromptType.Template) {
                    invalidateTemplateInstanceCache(updated.key || entity.key);
                  }

                  // The reload only refreshes the page currently being requested, which need not be
                  // the one this row sits on, so the saved row is written back on its own.
                  replacePromptInLocalState(updated);
                  setEditPrompt(undefined);
                  setEditPromptId(undefined);
                  await loadMonitoringPromptPage();
                }}
              />
            ) : null}
          </div>
        );
      }

      const promptContainerClass = flat
        ? 'p-5'
        : `${BP_CARD_CLASSES} p-5 transition-shadow duration-200 hover:shadow-card-hover ${nested ? 'ml-6' : ''}`;

      return (
        <div key={prompt.id} className={promptContainerClass}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="m-0 truncate text-base font-bold text-slate-900">{prompt.name}</h3>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusClass(prompt.status)}`}
                >
                  {translateEnumValue('MonitoringPromptStatus', prompt.status)}
                </span>
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                  {translateEnumValue('MonitoringPromptType', prompt.type)}
                </span>
              </div>

              <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{prompt.promptText}</p>

              <div className="flex flex-wrap gap-2 mt-3">
                {prompt.promptLocale?.getStableKey?.() ? (
                  <TaxonomyTermNameBadge
                    api={api}
                    termKey={prompt.promptLocale.getStableKey()}
                    applyFacetPresentation
                    termFacet={TaxonomyFacet.Locale}
                  />
                ) : null}
                {(prompt.engines || []).map((engine) => (
                  <TaxonomyTermNameBadge
                    key={engine.getStableKey()}
                    api={api}
                    termKey={engine.getStableKey()}
                    applyFacetPresentation
                    termFacet={TaxonomyFacet.Engine}
                  />
                ))}
                {(prompt.templateTerms || []).slice(0, 4).map((term) => (
                  <TaxonomyTermNameBadge key={term.getStableKey()} api={api} termKey={term.getStableKey()} applyFacetPresentation />
                ))}
              </div>

              <div className="mt-3 text-xs tabular-nums text-slate-500">
                Last run: {formatDate(prompt.lastRun)} · Next run: {formatDate(prompt.nextRun)} · Every {prompt.frequencyHours}h
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                onClick={() => void refreshPrompt(prompt, { activateInlineEdit: true })}
              >
                Edit
              </button>

              {prompt.type !== MonitoringPromptType.Template ? (
                <button
                  type="button"
                  className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                  onClick={() => navigate(`/brand-presence/settings/monitoring/${encodeURIComponent(String(prompt.id))}/runs`)}
                >
                  Runs
                </button>
              ) : null}

              <Dropdown align="end">
                <Dropdown.Toggle size="sm" variant="light" className="rounded-lg border-slate-200">
                  •••
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {prompt.type !== MonitoringPromptType.Template ? (
                    <Dropdown.Item
                      onClick={() => navigate(`/brand-presence/settings/monitoring/${encodeURIComponent(String(prompt.id))}/run`)}
                    >
                      Run prompt
                    </Dropdown.Item>
                  ) : null}
                  {canUpdatePromptStatus(prompt)
                    ? getPromptStatusActions(prompt.status).map((action) => (
                        <Dropdown.Item
                          key={`${prompt.id}-${action.nextStatus}`}
                          disabled={!!statusUpdateByPromptId[String(prompt.id)]}
                          onClick={() => void updatePromptStatus(prompt, action.nextStatus)}
                        >
                          {action.label}
                        </Dropdown.Item>
                      ))
                    : null}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      );
    },
    [
      api,
      editPrompt,
      editPromptId,
      engineOptions,
      loadMonitoringPromptPage,
      localeOptions,
      navigate,
      refreshPrompt,
      replacePromptInLocalState,
      statusUpdateByPromptId,
      templateTermOptions,
      invalidateTemplateInstanceCache,
      updateMonitoringPrompt,
      updatePromptStatus,
    ]
  );

  const renderTemplateInstanceRow = React.useCallback(
    (template: ClientMonitoringPromptEntity, instance: ClientMonitoringPromptEntity) => {
      const isInlineEditing = !!editPromptId && String(editPromptId) === String(instance.id) && !!editPrompt;

      if (isInlineEditing && editPrompt) {
        return (
          <div key={instance.id} className="border-t border-slate-200/70 bg-white p-5">
            {renderPromptCard(instance, { flat: true })}
          </div>
        );
      }

      return (
        <div key={instance.id} className="border-t border-slate-100 bg-white px-5 py-3 transition-colors duration-150 hover:bg-slate-50/60">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm font-semibold text-slate-900 truncate">
                  <MonitoringPromptInstanceName
                    prompt={instance}
                    template={template}
                    localeNameByKey={localeNameByKey}
                    termNameByKey={termNameByKey}
                  />
                </h4>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusClass(instance.status)}`}
                >
                  {translateEnumValue('MonitoringPromptStatus', instance.status)}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{instance.promptText}</p>
              <div className="text-xs text-slate-500 mt-2">
                Last run: {formatDate(instance.lastRun)} · Next run: {formatDate(instance.nextRun)}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                onClick={() => void refreshPrompt(instance, { activateInlineEdit: true })}
              >
                Edit
              </button>

              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                onClick={() => navigate(`/brand-presence/settings/monitoring/${encodeURIComponent(String(instance.id))}/runs`)}
              >
                Runs
              </button>

              <Dropdown align="end">
                <Dropdown.Toggle size="sm" variant="light" className="rounded-lg border-slate-200">
                  •••
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => navigate(`/brand-presence/settings/monitoring/${encodeURIComponent(String(instance.id))}/run`)}
                  >
                    Run prompt
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      );
    },
    [
      editPrompt,
      editPromptId,
      localeNameByKey,
      navigate,
      refreshPrompt,
      renderPromptCard,
      statusUpdateByPromptId,
      termNameByKey,
      updatePromptStatus,
    ]
  );

  React.useEffect(() => {
    if (routeState.mode !== 'run' || !routePrompt || !executeTaskGroupId || !api) {
      return;
    }

    if (executePollingRef.current) {
      clearInterval(executePollingRef.current);
      executePollingRef.current = undefined;
    }

    executePollingRef.current = setInterval(() => {
      void (async () => {
        try {
          const [summary, latestRunList] = await Promise.all([
            api.service.taskGroups.summary(executeTaskGroupId),
            api.governance.monitoringPromptRuns.list(
              { page: 0, itemsPerPage: 1 },
              {
                promptKey: routePrompt.key,
              }
            ),
          ]);

          const latestRun = latestRunList.items?.[0];
          setExecuteLatestRun(latestRun);

          if (latestRun?.id) {
            if (executePollingRef.current) {
              clearInterval(executePollingRef.current);
              executePollingRef.current = undefined;
            }

            navigate(
              `/brand-presence/settings/monitoring/${encodeURIComponent(String(routePrompt.id || routeState.promptId || ''))}/runs/${encodeURIComponent(
                String(latestRun.id)
              )}`,
              { replace: true }
            );
            return;
          }

          const statusText = (summary.byStatus || [])
            .map((entry) => `${translateEnumValue('TaskStatus', String(entry.status || ''))}: ${entry.count}`)
            .join(' · ');
          setExecuteProgress(statusText || 'Waiting for task status…');

          const isAnyRunning = (summary.byStatus || []).some((entry) => isTaskStatusRunning(entry.status as string));
          if (!isAnyRunning) {
            if (executePollingRef.current) {
              clearInterval(executePollingRef.current);
              executePollingRef.current = undefined;
            }
            setExecuteLoading(false);
          }
        } catch (pollError) {
          setExecuteLoading(false);
          setExecuteError((pollError as Error)?.message || 'Failed to poll execution progress.');
          if (executePollingRef.current) {
            clearInterval(executePollingRef.current);
            executePollingRef.current = undefined;
          }
        }
      })();
    }, 3000);

    return () => {
      if (executePollingRef.current) {
        clearInterval(executePollingRef.current);
        executePollingRef.current = undefined;
      }
    };
  }, [api, executeTaskGroupId, navigate, routePrompt, routeState.mode, routeState.promptId]);

  const startPromptExecution = React.useCallback(async () => {
    if (!api || !routePrompt) {
      return;
    }

    setExecuteLoading(true);
    setExecuteError(undefined);
    setExecuteProgress(undefined);
    setExecuteLatestRun(undefined);

    try {
      const executeProjectId = routePrompt.project?.getId?.();
      const site = await resolveProjectSite({ api, projectId: executeProjectId });

      if (!site) {
        throw new Error('No site is available for the selected prompt project.');
      }

      const draft = new ClientTaskGroupDraft();
      draft.type = TaskGroupType.MonitorPrompts;
      Object.assign(draft, {
        site: new StaticReference(site),
        monitoringPrompt: routePrompt.id ? new StaticReference(routePrompt) : undefined,
      });

      const taskGroup = await api.service.taskGroups.create(draft);
      setExecuteTaskGroupId(taskGroup.id as InternalId);
      setExecuteProgress('Queued monitoring prompt run…');
    } catch (executionError) {
      setExecuteLoading(false);
      setExecuteError((executionError as Error)?.message || 'Failed to run prompt.');
    }
  }, [api, routePrompt]);

  React.useEffect(() => {
    if (routeState.mode === 'run') {
      return;
    }

    setExecuteLoading(false);
    setExecuteError(undefined);
    setExecuteProgress(undefined);
    setExecuteTaskGroupId(undefined);
    setExecuteLatestRun(undefined);

    if (executePollingRef.current) {
      clearInterval(executePollingRef.current);
      executePollingRef.current = undefined;
    }
  }, [routeState.mode]);

  const navigateToMonitoringPromptsList = React.useCallback(() => {
    navigate('/brand-presence/settings/monitoring');
  }, [navigate]);

  const hasMoreToLoad = !!response && response.page < response.numberOfPages - 1;

  return (
    <div className="relative w-full h-full bg-slate-50 bg-brand-page">
      <div className="relative max-w-[1548px] mx-auto space-y-5 px-6 py-8">
        <BrandPresencePageHeader
          eyebrow={
            <>
              Brand Presence <span className="text-slate-300">/</span> Settings
            </>
          }
          title="Monitoring Prompts"
          description="Create templates or instances, review recent runs, and trigger prompt execution."
          actions={
            <button
              type="button"
              className={BP_PRIMARY_BUTTON_CLASSES}
              onClick={() => {
                setCreateDraft(defaultPromptDraft(syncCoreProjectId));
                setEditPrompt(undefined);
                setEditPromptId(undefined);
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
              Add prompt
            </button>
          }
        />

        <div className={`${BP_CARD_CLASSES} p-4 motion-safe:animate-bp-fade-up`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                Status
              </Form.Label>
              <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
                <option value="all">All</option>
                {NON_PREVIOUS_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {translateEnumValue('MonitoringPromptStatus', status)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Type</Form.Label>
              <Form.Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as TypeFilter)}>
                <option value="all">All</option>
                {Object.values(MonitoringPromptType).map((type) => (
                  <option key={type} value={type}>
                    {translateEnumValue('MonitoringPromptType', type)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                Country
              </Form.Label>
              <Form.Select
                value={countryFilter}
                onChange={(event) => setCountryFilter(event.target.value as MonitoringPromptCountry | 'all')}
              >
                <option value="all">All</option>
                {availableCountries.map((country) => (
                  <option key={country} value={country}>
                    {translateEnumValue('MonitoringPromptCountry', country)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                Engine
              </Form.Label>
              <Form.Select value={engineFilterKey} onChange={(event) => setEngineFilterKey(event.target.value)}>
                <option value="">All</option>
                {engineOptions.map((option) => (
                  <option key={option.id} value={option.key}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Form.Label className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                Language
              </Form.Label>
              <Form.Select value={localeFilterKey} onChange={(event) => setLocaleFilterKey(event.target.value)}>
                <option value="">All</option>
                {localeOptions.map((option) => (
                  <option key={option.id} value={option.key}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>

        {loading && !loadedPrompts.length ? (
          <SkeletonRegion label="Loading monitoring prompts…" className="space-y-4">
            {PROMPT_SKELETON_CARDS}
          </SkeletonRegion>
        ) : null}
        {error ? <div className="text-sm text-rose-600">{error}</div> : null}

        {!loading && !error && routeState.mode !== 'list' ? (
          <div className={`${BP_CARD_CLASSES} space-y-4 p-5 motion-safe:animate-bp-fade-up`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="m-0 text-base font-bold text-slate-900">
                  {routeState.mode === 'runs' ? 'Prompt runs' : 'Run prompt'}
                  {routePrompt ? (
                    <>
                      {' · '}
                      <MonitoringPromptInstanceName prompt={routePrompt} localeNameByKey={localeNameByKey} termNameByKey={termNameByKey} />
                    </>
                  ) : null}
                </h2>
              </div>
              <button type="button" className={BP_TOOLBAR_BUTTON_SMALL_CLASSES} onClick={navigateToMonitoringPromptsList}>
                Back to prompts
              </button>
            </div>

            {routePromptLoading ? (
              <SkeletonRegion label="Loading prompt…" className="space-y-3">
                <SkeletonBlock className="h-4 w-72 max-w-full" />
                <SkeletonBlock className="h-32 w-full rounded-xl" />
              </SkeletonRegion>
            ) : null}
            {routePromptError ? <div className="text-sm text-rose-600">{routePromptError}</div> : null}

            {!routePromptLoading && !routePromptError && routePrompt && routeState.mode === 'runs' ? (
              <div className="space-y-4">
                <div className="overflow-hidden rounded-xl ring-1 ring-inset ring-slate-200/80">
                  <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-slate-50/70 border-b border-slate-200/70 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <div className="col-span-2">Status</div>
                    <div className="col-span-3">Engine</div>
                    <div className="col-span-3">Scheduled</div>
                    <div className="col-span-3">Computed</div>
                    <div className="col-span-1 text-right"># citations</div>
                  </div>
                  {runsLoading ? (
                    <SkeletonRegion label="Loading runs…" className="divide-y divide-slate-100">
                      {RUN_SKELETON_ROWS}
                    </SkeletonRegion>
                  ) : null}
                  {runsError ? <div className="px-4 py-4 text-sm text-rose-600">{runsError}</div> : null}
                  {!runsLoading && !runsError && !runs.length ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-500">No runs found.</div>
                  ) : null}
                  {!runsLoading && !runsError && runs.length ? (
                    <div className="divide-y divide-slate-100">
                      {runs.map((run) => {
                        const selected = selectedRun?.id === run.id;
                        return (
                          <button
                            key={run.id}
                            type="button"
                            className={`grid w-full grid-cols-12 items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors duration-150 ${
                              selected ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : 'bg-white hover:bg-slate-50/80'
                            }`}
                            onClick={() =>
                              navigate(
                                `/brand-presence/settings/monitoring/${encodeURIComponent(String(routeState.promptId || routePrompt.id))}/runs/${encodeURIComponent(String(run.id))}`
                              )
                            }
                          >
                            <div className="col-span-2">
                              <MonitoringPromptRunStatusBadge status={run.status as MonitoringPromptRunStatus} />
                            </div>
                            <div className="col-span-3 truncate">
                              {getStableKey(run.engine) ? (
                                <TaxonomyTermNameBadge
                                  api={api}
                                  termKey={getStableKey(run.engine)}
                                  applyFacetPresentation
                                  termFacet={TaxonomyFacet.Engine}
                                />
                              ) : (
                                '-'
                              )}
                            </div>
                            <div className="col-span-3 truncate">{run.scheduledFor?.format('MMM DD, HH:mm') || '-'}</div>
                            <div className="col-span-3 truncate">{run.computedAt?.format('MMM DD, HH:mm') || '-'}</div>
                            <div className="col-span-1 text-right">{citationCountByRunId[String(run.id)] ?? 0}</div>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center justify-between text-sm text-slate-500">
                  <div className="tabular-nums">
                    Page {runsPage + 1} of {runsNumberOfPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                      disabled={runsPage <= 0}
                      onClick={() => setRunsPage((value) => Math.max(0, value - 1))}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                      disabled={runsPage >= runsNumberOfPages - 1}
                      onClick={() => setRunsPage((value) => Math.min(runsNumberOfPages - 1, value + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>

                {/* The prompt settings screen holds no purpose tag: its term options carry a name and
                    a facet, never a purpose type, so there is no mask to derive without loading the
                    tag itself. Unmasked is what it renders today, stated rather than inherited. */}
                <MonitoringPromptRunDetail run={selectedRun} api={api} kpiMask={NO_PURPOSE_KPI_MASK} />
              </div>
            ) : null}

            {!routePromptLoading && !routePromptError && routePrompt && routeState.mode === 'run' ? (
              <>
                {executeError ? <div className="text-sm text-rose-600">{executeError}</div> : null}

                <div className="text-sm text-slate-600">
                  {executeLoading
                    ? executeProgress || 'Running prompt…'
                    : executeTaskGroupId
                      ? 'Execution finished. Latest run result is shown below.'
                      : 'Start a monitoring run for this prompt and watch progress.'}
                </div>

                {/* Same as above: no purpose tag is in scope on this screen. */}
                <MonitoringPromptRunDetail run={executeLatestRun} api={api} kpiMask={NO_PURPOSE_KPI_MASK} />

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                    onClick={navigateToMonitoringPromptsList}
                    disabled={executeLoading}
                  >
                    Close
                  </button>
                  {!executeTaskGroupId ? (
                    <button
                      type="button"
                      className={BP_PRIMARY_BUTTON_SMALL_CLASSES}
                      onClick={() => void startPromptExecution()}
                      disabled={executeLoading}
                    >
                      {executeLoading ? 'Running…' : 'Run now'}
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {!error && routeState.mode === 'list' && (loadedPrompts.length > 0 || !loading) ? (
          <>
            {createDraft ? (
              <div className={`${BP_CARD_CLASSES} p-5 motion-safe:animate-bp-fade-up`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="m-0 text-base font-bold text-slate-900">Create monitoring prompt</h3>
                  <button type="button" className={BP_TOOLBAR_BUTTON_SMALL_CLASSES} onClick={() => setCreateDraft(undefined)}>
                    Cancel
                  </button>
                </div>

                {api ? (
                  <MonitoringPromptDraftEditor
                    api={api}
                    initialValues={createDraft}
                    submitLabel="Create"
                    taxonomyOptions={taxonomyOptions}
                    localeOptions={localeOptions}
                    availableCountries={availableCountries}
                    engineOptions={engineOptions}
                    templateTermOptions={templateTermOptions}
                    onSubmit={async (draft) => {
                      ensureMonitoringPromptDraftDefaults(draft);
                      await api.governance.monitoringPrompts.create(draft);
                      setCreateDraft(undefined);
                      await loadMonitoringPromptPage();
                    }}
                  />
                ) : null}
              </div>
            ) : null}

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h2 className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Prompts</h2>
                <span className="inline-flex items-center rounded-full bg-slate-900/5 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
                  {promptItems.length}
                </span>
              </div>

              {!promptItems.length ? (
                <div className={BP_CARD_CLASSES}>
                  <BrandPresenceEmptyState
                    icon={faSatelliteDish}
                    title="No prompts found."
                    description="Add a prompt to start tracking how AI engines answer about your brand."
                  />
                </div>
              ) : null}

              {promptItems.map((prompt) => {
                const isTemplate = prompt.type === MonitoringPromptType.Template;
                const templateKey = prompt.key;
                const expanded = !!expandedTemplateKeys[templateKey];
                const instances = templateInstances[templateKey] || [];
                const instancesLoading = !!templateInstanceLoading[templateKey];
                const instanceCount = templateInstanceCounts[templateKey];

                return (
                  <div key={prompt.id}>
                    {!isTemplate ? (
                      renderPromptCard(prompt)
                    ) : (
                      <div className={`${BP_CARD_CLASSES} overflow-hidden`}>
                        <div className="border-b border-slate-200/70">{renderPromptCard(prompt, { flat: true })}</div>

                        <button
                          type="button"
                          className="w-full border-b border-slate-200/70 bg-slate-50/70 px-5 py-3 text-left transition-colors duration-150 hover:bg-slate-100/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                          onClick={() => void toggleTemplateExpansion(prompt)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex flex-wrap gap-2">
                              {prompt.templateFacet ? (
                                <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
                                  Templated: {translateEnumValue('TaxonomyFacet', prompt.templateFacet)}
                                </span>
                              ) : null}
                              <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-sky-700 ring-1 ring-inset ring-sky-200">
                                Instances: {instanceCount !== undefined ? instanceCount : '...'}
                              </span>
                            </div>
                            <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
                              {expanded ? 'Hide instances' : 'Show instances'}
                              <svg
                                aria-hidden="true"
                                viewBox="0 0 20 20"
                                className="h-3.5 w-3.5 transition-transform duration-200"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                {expanded ? <path d="M5 12l5-5 5 5" /> : <path d="M5 8l5 5 5-5" />}
                              </svg>
                            </span>
                          </div>
                        </button>

                        {expanded ? (
                          <div>
                            {instancesLoading ? (
                              <SkeletonRegion label="Loading instances…" className="divide-y divide-slate-100">
                                {INSTANCE_SKELETON_ROWS}
                              </SkeletonRegion>
                            ) : null}
                            {!instancesLoading && !instances.length ? (
                              <div className="px-5 py-3 text-xs text-slate-500">No instances found for this template.</div>
                            ) : null}
                            {!instancesLoading ? instances.map((instance) => renderTemplateInstanceRow(prompt, instance)) : null}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {response ? (
              <div className="flex items-center justify-between text-sm text-slate-500 pt-2">
                <div>
                  <span className="tabular-nums">
                    Showing {loadedPrompts.length} of {response.totalNumberOfItems} prompts
                  </span>
                </div>
                {hasMoreToLoad ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className={BP_TOOLBAR_BUTTON_CLASSES}
                      disabled={loading}
                      onClick={() => setPage((value) => value + 1)}
                    >
                      {loading ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
